// client/src/api/ApiEmailOutbox.js
import flex from './FlexFetch'

const endpoint = '/api/1.0/email-outbox/'

function getPayloadData(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload
  }
  return payload.data ?? payload
}

/**
 * Generate email drafts for a league (idempotent)
 * @param {number} leagueId - League ID
 * @param {string|null} cycleKey - Optional cycle key (defaults to current week)
 * @returns {Promise} Result with draftsCreated, duplicatesSkipped, cycleKey, stateBreakdown
 */
async function generateDrafts(leagueId, cycleKey = null) {
  const response = await flex.flexFetch(endpoint + 'generate', {
    method: 'POST',
    body: JSON.stringify({ league_id: leagueId, cycle_key: cycleKey }),
  })
  return getPayloadData(await flex.handleResponse(response))
}

/**
 * Get filtered list of email drafts
 * @param {number} leagueId - League ID
 * @param {Object} filters - Optional filters (status, template_key, cycle_key, start_date, end_date)
 * @returns {Promise} Array of draft objects
 */
async function getDrafts(leagueId, filters = {}) {
  const params = new URLSearchParams({ league_id: leagueId, ...filters })
  const response = await flex.flexFetch(endpoint + `list?${params}`)
  return getPayloadData(await flex.handleResponse(response))
}

/**
 * Send test email to override recipient (does NOT mark draft as sent)
 * @param {number} draftId - Draft ID
 * @param {string} testEmail - Test recipient email
 * @returns {Promise} Result with messageId
 */
async function testSend(draftId, testEmail) {
  const response = await flex.flexFetch(endpoint + 'test-send', {
    method: 'POST',
    body: JSON.stringify({ draft_id: draftId, test_email: testEmail }),
  })
  return getPayloadData(await flex.handleResponse(response))
}

/**
 * Send single email draft (requires SEND_EMAILS_ENABLED=true)
 * @param {number} draftId - Draft ID
 * @returns {Promise} Result with messageId, sentTo
 */
async function send(draftId) {
  const response = await flex.flexFetch(endpoint + 'send', {
    method: 'POST',
    body: JSON.stringify({ draft_id: draftId }),
  })
  return getPayloadData(await flex.handleResponse(response))
}

/**
 * Send batch of email drafts (requires SEND_EMAILS_ENABLED=true)
 * @param {number} leagueId - League ID
 * @param {Object} filters - Optional filters (status, template_key, max_errors, batch_size)
 * @returns {Promise} Result with sent, failed, skipped counts and errors array
 */
async function sendBatch(leagueId, filters = {}) {
  const response = await flex.flexFetch(endpoint + 'send-batch', {
    method: 'POST',
    body: JSON.stringify({ league_id: leagueId, ...filters }),
  })
  return getPayloadData(await flex.handleResponse(response))
}

/**
 * Cancel an email draft (changes status to 'canceled')
 * @param {number} draftId - Draft ID
 * @returns {Promise} Success result
 */
async function cancel(draftId) {
  const response = await flex.flexFetch(endpoint + 'cancel', {
    method: 'POST',
    body: JSON.stringify({ draft_id: draftId }),
  })
  return getPayloadData(await flex.handleResponse(response))
}

/**
 * Retry a failed draft (resets status from 'failed' to 'draft')
 * @param {number} draftId - Draft ID
 * @returns {Promise} Success result
 */
async function retry(draftId) {
  const response = await flex.flexFetch(endpoint + 'retry', {
    method: 'POST',
    body: JSON.stringify({ draft_id: draftId }),
  })
  return getPayloadData(await flex.handleResponse(response))
}

const ApiEmailOutbox = {
  generateDrafts,
  getDrafts,
  testSend,
  send,
  sendBatch,
  cancel,
  retry,
}

export default ApiEmailOutbox
